import { useCallback, useState } from 'react';
import { advanceWorkflow, type OutgoingAction } from '../../../../services/workQueue';
import {
    CLINICAL_VA_NEEDED_STATUS_ID,
    DM_VA_NEEDED_STATUS_ID,
    TREATMENT_IN_PROGRESS_STATUS_ID
} from '../../constants';
import { useInvalidatePatientVirtualAppointments } from '../../../../hooks/useVirtualAppointments';
import { apiErrorMessage } from '../../../../utils/apiError';

export type VaHandoffKind = 'escalate-to-clinical' | 'hand-back-to-dm' | 'continue-treatment';

// A switch between the DM and Clinical steps carries the author's text; continuing posts nothing.
export type VaReasonHandoffKind = Exclude<VaHandoffKind, 'continue-treatment'>;
export type VaPlainHandoffKind = Exclude<VaHandoffKind, VaReasonHandoffKind>;

export type VaHandoffRequest =
    { kind: VaReasonHandoffKind; reason: string } | { kind: VaPlainHandoffKind };

// The manual moves out of a VA step. The engine marks these automated, so the status meta
// doesn't list them; the label is what the completion page shows.
const HANDOFF_ACTIONS: Record<VaHandoffKind, OutgoingAction> = {
    'escalate-to-clinical': {
        action: 'clinical-va-due',
        label: 'Escalated to Clinical',
        actor: 'internal',
        direction: 'lateral',
        to: CLINICAL_VA_NEEDED_STATUS_ID
    },
    'hand-back-to-dm': {
        action: 'clinical-vas-resolved',
        label: 'Handed back to DM',
        actor: 'internal',
        direction: 'forward',
        to: DM_VA_NEEDED_STATUS_ID
    },
    'continue-treatment': {
        action: 'vas-resolved',
        label: 'Treatment continued',
        actor: 'internal',
        direction: 'forward',
        to: TREATMENT_IN_PROGRESS_STATUS_ID
    }
};

const FALLBACK_ERRORS: Record<VaHandoffKind, string> = {
    'escalate-to-clinical': "Couldn't escalate the case to Clinical. Try again.",
    'hand-back-to-dm': "Couldn't hand the case back to DM. Try again.",
    'continue-treatment': "Couldn't continue treatment. Try again."
};

export interface VaHandoff {
    submitting: boolean;
    error: string | null;
    run: (request: VaHandoffRequest) => Promise<void>;
    resetError: () => void;
}

function handoffBody(request: VaHandoffRequest): Record<string, unknown> | undefined {
    return 'reason' in request ? { reason: request.reason.trim() } : undefined;
}

// Refetches the VAs and their round either way: success moves the case, a 409 means they changed.
export function useVaHandoff(
    patientId: string,
    onTaskComplete?: (action?: OutgoingAction) => void
): VaHandoff {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const refreshVas = useInvalidatePatientVirtualAppointments(patientId);

    const run = useCallback(
        async (request: VaHandoffRequest) => {
            setSubmitting(true);
            setError(null);
            try {
                const completed = HANDOFF_ACTIONS[request.kind];
                await advanceWorkflow(patientId, completed.action, handoffBody(request));
                refreshVas();
                onTaskComplete?.(completed);
            } catch (err) {
                setError(apiErrorMessage(err, FALLBACK_ERRORS[request.kind]));
                setSubmitting(false);
                refreshVas();
            }
        },
        [patientId, onTaskComplete, refreshVas]
    );
    const resetError = useCallback(() => setError(null), []);
    return { submitting, error, run, resetError };
}
